import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  static #SWIPE_THRESHOLD = 60;

  #swipeStartY = 0;
  #swipeCurrentY = 0;
  #swipeActive = false;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchStartHandler = null;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchMoveHandler = null;
  /** @type {((e: TouchEvent) => void) | null} */
  #swipeTouchEndHandler = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    if (this.refs.dialog?.open && !this.#isNestedDialog) {
      this.#stopBodyStyleObserver();
      document.body.style.width = this.#savedBodyWidth || '';
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      this.#removeTouchMoveLock();
      this.#savedBodyWidth = '';
      this.#scrollLockWidthPx = 0;
    }
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** Content width in px when showDialog ran; used to restore on close and re-apply if cleared. */
  #savedBodyWidth = '';

  /** Width we set on body for scroll-lock (px); re-applied if something clears it while dialog is open. */
  #scrollLockWidthPx = 0;

  /** @type {MutationObserver | null} Re-applies body width when content updates (e.g. Constructor filter) clear it. */
  #bodyStyleObserver = null;

  /** Timestamp when dialog was last opened; used to ignore spurious "click outside" right after open (e.g. same tap on mobile). */
  #openedAt = 0;

  /** @type {HTMLElement | null} Element that had focus before the dialog opened; restored on close. */
  #openerElement = null;

  /** True when this dialog opened while another dialog already had scroll-lock active. */
  #isNestedDialog = false;

  /** @type {((e: TouchEvent) => void) | null} */
  #scrollLockTouchHandler = null;

  /**
   * Applies scroll-lock body styles. Idempotent; safe to call when already applied.
   */
  #applyScrollLockBodyStyles() {
    if (this.#scrollLockWidthPx > 0) {
      document.body.style.width = `${this.#scrollLockWidthPx}px`;
    }
  }

  /**
   * Starts observing body.style so we can re-apply width if it is cleared while the dialog is open.
   */
  #startBodyStyleObserver() {
    if (this.#bodyStyleObserver || !document.body) return;
    this.#bodyStyleObserver = new MutationObserver(() => {
      if (!this.refs.dialog?.open || this.#scrollLockWidthPx <= 0) return;
      if (document.body.style.width !== `${this.#scrollLockWidthPx}px`) {
        this.#applyScrollLockBodyStyles();
      }
    });
    this.#bodyStyleObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['style'],
    });
  }

  /**
   * Stops the body style observer and clears the reference.
   */
  #stopBodyStyleObserver() {
    this.#bodyStyleObserver?.disconnect();
    this.#bodyStyleObserver = null;
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Capture content width before scroll lock so we can pin body width
    // (prevents layout shift when scrollbar disappears with overflow:hidden).
    const contentWidth = document.documentElement.clientWidth;

    const alreadyScrollLocked = document.documentElement.style.overflow === 'hidden';
    this.#isNestedDialog = alreadyScrollLocked;

    this.#openerElement = /** @type {HTMLElement | null} */ (document.activeElement);

    // Show the dialog and focus synchronously so iOS recognises the focus as
    // part of the original user-gesture chain and opens the keyboard.
    // Deferring focus into rAF breaks that chain on iOS.
    dialog.showModal();
    dialog.setAttribute('tabindex', '-1');
    const autofocusEl = /** @type {HTMLElement | null} */ (dialog.querySelector('[autofocus]'));
    if (autofocusEl) {
      autofocusEl.focus();
    } else {
      dialog.focus();
    }
    this.#openedAt = Date.now();

    // Separate DOM writes (scroll lock) into rAF to avoid layout thrashing
    requestAnimationFrame(() => {
      if (!alreadyScrollLocked) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        if (contentWidth > 0) {
          this.#savedBodyWidth = document.body.style.width || '';
          this.#scrollLockWidthPx = contentWidth;
          document.body.style.width = `${contentWidth}px`;
          this.#startBodyStyleObserver();
        }
        this.#addTouchMoveLock();
      }

      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      if (this.hasAttribute('swipe-to-close')) {
        this.#bindSwipeListeners();
      }
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#openedAt = 0;

    this.#unbindSwipeListeners();
    dialog.style.transform = '';
    dialog.style.transition = '';

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (this.#isNestedDialog) {
      this.#isNestedDialog = false;
    } else {
      this.#stopBodyStyleObserver();
      document.body.style.width = this.#savedBodyWidth;
      this.#savedBodyWidth = '';
      this.#scrollLockWidthPx = 0;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      this.#removeTouchMoveLock();
      // If something (e.g. a filter re-render) scrolled the page upward while the
      // dialog was open, respect that new position instead of snapping back.
      if (!document.documentElement.hasAttribute('data-scrolling-to-top')) {
        window.scrollTo({
          top: Math.min(window.scrollY, this.#previousScrollY),
          behavior: 'instant',
        });
      }
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.#openerElement?.focus();
    this.#openerElement = null;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  #bindSwipeListeners() {
    const { dialog } = this.refs;
    this.#swipeTouchStartHandler = this.#handleSwipeTouchStart.bind(this);
    this.#swipeTouchMoveHandler = this.#handleSwipeTouchMove.bind(this);
    this.#swipeTouchEndHandler = this.#handleSwipeTouchEnd.bind(this);
    dialog.addEventListener('touchstart', this.#swipeTouchStartHandler, { passive: true });
    dialog.addEventListener('touchmove', this.#swipeTouchMoveHandler, { passive: false });
    dialog.addEventListener('touchend', this.#swipeTouchEndHandler, { passive: true });
  }

  #unbindSwipeListeners() {
    const { dialog } = this.refs;
    if (!dialog) return;
    if (this.#swipeTouchStartHandler)
      dialog.removeEventListener('touchstart', this.#swipeTouchStartHandler);
    if (this.#swipeTouchMoveHandler)
      dialog.removeEventListener('touchmove', this.#swipeTouchMoveHandler);
    if (this.#swipeTouchEndHandler)
      dialog.removeEventListener('touchend', this.#swipeTouchEndHandler);
    this.#swipeTouchStartHandler = null;
    this.#swipeTouchMoveHandler = null;
    this.#swipeTouchEndHandler = null;
  }

  /**
   * @param {TouchEvent} e
   */
  #handleSwipeTouchStart(e) {
    const target = e.target instanceof Element ? e.target : null;
    const drawer = target?.closest('dialog, dialog-component') || null;
    const content = drawer?.querySelector('[ref="content"]') || null;

    // Don't allow swipe if the content is scrolled down
    // or if the content is not scrollable, check that the dialog is not scrolled down
    if (
      !drawer ||
      (drawer && content && content.scrollTop > 0) ||
      (drawer && !content && drawer.scrollTop > 0)
    ) {
      this.#swipeActive = false;
      return;
    }

    this.#swipeActive = true;
    this.#swipeStartY = e.touches[0].clientY;
    this.#swipeCurrentY = this.#swipeStartY;
  }

  /**
   * @param {TouchEvent} e
   */
  #handleSwipeTouchMove(e) {
    if (!this.#swipeActive) return;

    const deltaY = e.touches[0].clientY - this.#swipeStartY;
    if (deltaY <= 0) return;

    e.preventDefault();
    this.#swipeCurrentY = e.touches[0].clientY;

    if (!prefersReducedMotion()) {
      this.refs.dialog.style.transform = `translateY(${deltaY}px)`;
    }
  }

  #handleSwipeTouchEnd() {
    if (!this.#swipeActive) return;

    const deltaY = this.#swipeCurrentY - this.#swipeStartY;

    if (deltaY >= DialogComponent.#SWIPE_THRESHOLD) {
      this.refs.dialog.style.transform = '';
      this.closeDialog();
    } else {
      const { dialog } = this.refs;
      dialog.style.transition = 'transform 0.2s ease';
      dialog.style.transform = '';
      dialog.addEventListener(
        'transitionend',
        () => {
          dialog.style.transition = '';
        },
        { once: true },
      );
    }
  }

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      if (this.#openedAt && Date.now() - this.#openedAt < 150) return;
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
      return;
    }

    if (event.key === 'Tab') {
      this.#trapFocus(event);
    }
  }

  #trapFocus(event) {
    const { dialog } = this.refs;
    const focusable = [
      ...dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((el) => {
      const style = getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden' && !el.closest('[hidden]');
    });

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (event.target === first || event.target === dialog) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (event.target === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }

  #addTouchMoveLock() {
    if (this.#scrollLockTouchHandler) return;
    this.#scrollLockTouchHandler = (e) => {
      if (!(e.target instanceof Element)) {
        e.preventDefault();
        return;
      }
      // Allow touch-scrolling inside scrollable elements within the dialog
      // (e.g. product grids, cart item lists). Walk up from the touch target;
      // if we find an element that is scrollable before we reach the <dialog>,
      // let the event through so native scroll works.
      let el = e.target;
      while (el && el.tagName !== 'DIALOG') {
        if (
          el.scrollHeight > el.clientHeight &&
          getComputedStyle(el).overflowY !== 'hidden' &&
          getComputedStyle(el).overflowY !== 'visible'
        ) {
          return;
        }
        if (
          el.scrollWidth > el.clientWidth &&
          getComputedStyle(el).overflowX !== 'hidden' &&
          getComputedStyle(el).overflowX !== 'visible'
        ) {
          return;
        }
        el = el.parentElement;
      }
      e.preventDefault();
    };
    document.addEventListener('touchmove', this.#scrollLockTouchHandler, { passive: false });
  }

  #removeTouchMoveLock() {
    if (this.#scrollLockTouchHandler) {
      document.removeEventListener('touchmove', this.#scrollLockTouchHandler);
      this.#scrollLockTouchHandler = null;
    }
  }
}

if (!customElements.get('dialog-component'))
  customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true },
);
